import { lazy, Suspense, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Navigation from '@/components/Navigation';
import Hero from '@/components/Hero';
import { useLanguage } from '@/contexts/LanguageContext';

// Lazy load ALL below-fold components for better LCP and reduced bundle size
const HeroGallery = lazy(() => import('@/components/HeroGallery'));
const About = lazy(() => import('@/components/About'));
const Testimonials = lazy(() => import('@/components/Testimonials'));
const FAQ = lazy(() => import('@/components/FAQ'));
const VagasMotorista = lazy(() => import('@/components/VagasMotorista'));
const DriverSection = lazy(() => import('@/components/DriverSection'));
const CoverageAreas = lazy(() => import('@/components/CoverageAreas'));
const QuickLinks = lazy(() => import('@/components/QuickLinks'));
const LazyBlogSection = lazy(() => import('@/components/LazyBlogSection'));
const Footer = lazy(() => import('@/components/Footer'));
const FloatingWhatsApp = lazy(() => import('@/components/FloatingWhatsApp'));

const Index = () => {
  const { language, t } = useLanguage();

  const breadcrumbSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": language === 'pt' ? "Início" : "Home", "item": "https://www.kareemtaxi.co.mz/" },
      { "@type": "ListItem", "position": 2, "name": language === 'pt' ? "Sobre Nós" : "About Us", "item": "https://www.kareemtaxi.co.mz/#about" },
      { "@type": "ListItem", "position": 3, "name": language === 'pt' ? "Cadastro Motorista Yango" : "Yango Driver Registration", "item": "https://www.kareemtaxi.co.mz/#cadastro" },
      { "@type": "ListItem", "position": 4, "name": language === 'pt' ? "Área de Motoristas" : "Drivers Area", "item": "https://www.kareemtaxi.co.mz/#drivers" },
      { "@type": "ListItem", "position": 5, "name": language === 'pt' ? "Depoimentos" : "Testimonials", "item": "https://www.kareemtaxi.co.mz/#testimonials" },
      { "@type": "ListItem", "position": 6, "name": language === 'pt' ? "FAQ - Perguntas Frequentes" : "FAQ - Frequently Asked Questions", "item": "https://www.kareemtaxi.co.mz/#faq" }
    ]
  }), [language]);

  const collectionPageSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": language === 'pt' ? "Blog e Recursos - Kareem Taxi" : "Blog and Resources - Kareem Taxi",
    "description": language === 'pt' 
      ? "Artigos e guias sobre ser motorista Yango em Maputo com Kareem Taxi" 
      : "Articles and guides about being a Yango driver in Maputo with Kareem Taxi",
    "url": "https://www.kareemtaxi.co.mz/blog"
  }), [language]);

  const organizationSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.kareemtaxi.co.mz/#org",
    "name": "KAREEM TAXI – Parceiro Oficial Yango",
    "alternateName": "Kareem Taxi Maputo",
    "url": "https://www.kareemtaxi.co.mz/",
    "logo": "https://www.kareemtaxi.co.mz/images/kareem-taxi-parceiro-oficial-yango-mocambique.webp",
    "telephone": "+258853448609",
    "description": language === 'pt'
      ? "Parceiro oficial Yango em Maputo. Cadastro rápido, recargas automáticas M-Pesa/eMola e suporte 24/7 para motoristas."
      : "Official Yango partner in Maputo. Fast registration, automatic M-Pesa/eMola recharges and 24/7 driver support.",
    "sameAs": [
      "https://www.facebook.com/kareemtaxi",
      "https://www.instagram.com/kareem.taxi/",
      "https://www.tiktok.com/@kareem.taxi",
      "https://www.pinterest.com/YangoKareemtaximz/",
      "https://x.com/Kareemtaximz"
    ],
    "contactPoint": [
      { "@type": "ContactPoint", "telephone": "+258853448609", "contactType": "customer support", "availableLanguage": ["Portuguese", "English"], "areaServed": "MZ" },
      { "@type": "ContactPoint", "telephone": "+258856764504", "contactType": "WhatsApp", "availableLanguage": ["Portuguese", "English"], "areaServed": "MZ" }
    ]
  }), [language]);

  const localBusinessSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "TaxiService"],
    "@id": "https://www.kareemtaxi.co.mz/#business",
    "parentOrganization": { "@id": "https://www.kareemtaxi.co.mz/#org" },
    "name": "KAREEM TAXI – Parceiro Oficial Yango",
    "alternateName": language === 'pt' ? "KAREEM TAXI – Parceiro Oficial Yango em Maputo" : "KAREEM TAXI – Official Yango Partner in Maputo",
    "foundingDate": "2024-03-15",
    "description": language === 'pt'
      ? "Parceiro oficial Yango em Maputo. Cadastro rápido, recargas automáticas M-Pesa/eMola, treinamento Yango Pro e suporte 24/7."
      : "Official Yango partner in Maputo. Fast registration, automatic M-Pesa/eMola recharges, Yango Pro training, 24/7 support.",
    "url": "https://www.kareemtaxi.co.mz/",
    "logo": "https://www.kareemtaxi.co.mz/images/kareem-taxi-parceiro-oficial-yango-mocambique.webp",
    "image": [
      "https://www.kareemtaxi.co.mz/images/kareem-taxi-parceiro-oficial-yango-mocambique.webp",
      "https://www.kareemtaxi.co.mz/images/motorista-yango-maputo-1024.avif",
      "https://www.kareemtaxi.co.mz/images/motorista-yango-tuktuk-kareem-taxi-maputo-1024.avif"
    ],
    "email": "info@kareemtaxi.co.mz",
    "address": { "@type": "PostalAddress", "streetAddress": "Av 24 de Julho, Alto Mae", "addressLocality": "Maputo", "addressRegion": "Maputo", "postalCode": "1204", "addressCountry": "MZ" },
    "geo": { "@type": "GeoCoordinates", "latitude": -25.97121935121613, "longitude": 32.58300286597757 },
    "areaServed": [
      { "@type": "City", "name": "Maputo", "containedIn": { "@type": "Country", "name": language === 'pt' ? "Moçambique" : "Mozambique" } },
      { "@type": "City", "name": "Matola", "containedIn": { "@type": "Country", "name": language === 'pt' ? "Moçambique" : "Mozambique" } },
      { "@type": "AdministrativeArea", "name": language === 'pt' ? "Distrito de Boane" : "Boane District", "containedIn": { "@type": "Country", "name": language === 'pt' ? "Moçambique" : "Mozambique" } },
      { "@type": "AdministrativeArea", "name": language === 'pt' ? "Distrito de Marracuene" : "Marracuene District", "containedIn": { "@type": "Country", "name": language === 'pt' ? "Moçambique" : "Mozambique" } }
    ],
    "priceRange": "$$",
    "currenciesAccepted": "MZN",
    "paymentAccepted": ["M-Pesa", "eMola", "Cash"],
    "amenityFeature": [{ "@type": "LocationFeatureSpecification", "name": language === 'pt' ? "Estacionamento disponível" : "Parking available", "value": true }],
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "08:00", "closes": "20:00", "description": language === 'pt' ? "Horário de escritório" : "Office hours" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"], "opens": "00:00", "closes": "23:59", "description": language === 'pt' ? "Suporte online disponível 24/7" : "Online support available 24/7" }
    ],
    "contactPoint": [
      { "@type": "ContactPoint", "telephone": "+258853448609", "contactType": "customer service", "email": "info@kareemtaxi.co.mz", "availableLanguage": ["Portuguese", "English"], "hoursAvailable": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "08:00", "closes": "20:00" } },
      { "@type": "ContactPoint", "telephone": "+258856764504", "contactType": "customer support", "contactOption": "TollFree", "availableLanguage": ["Portuguese", "English"], "hoursAvailable": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"], "opens": "00:00", "closes": "23:59" }, "description": language === 'pt' ? "Suporte WhatsApp 24/7" : "WhatsApp Support 24/7" },
      { "@type": "ContactPoint", "telephone": "+258863213663", "contactType": "sales", "availableLanguage": ["Portuguese", "English"], "hoursAvailable": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "08:00", "closes": "20:00" }, "description": language === 'pt' ? "Linha adicional para consultas" : "Additional inquiry line" }
    ],
    "sameAs": ["https://www.facebook.com/kareemtaxi.yango/", "https://www.instagram.com/kareem.taxi/", "https://www.tiktok.com/@kareem.taxi", "https://www.pinterest.com/YangoKareemtaxi/", "https://x.com/Kareemtaximz"]
  }), [language]);

  return (
    <>
      <Helmet>
        <html lang={language === 'pt' ? 'pt-MZ' : 'en'} />
        <title>{language === 'pt' ? 'KAREEM TAXI – Parceiro Oficial Yango em Maputo' : 'KAREEM TAXI – Official Yango Partner in Maputo'}</title>
        <meta 
          name="title" 
          content={language === 'pt' 
            ? 'KAREEM TAXI – Parceiro Oficial Yango em Maputo' 
            : 'KAREEM TAXI – Official Yango Partner in Maputo'
          } 
        />
        
        {/* Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        
        {/* Organization Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        
        {/* CollectionPage Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(collectionPageSchema)}
        </script>

        {/* LocalBusiness Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        
        {/* WebSite Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "KAREEM TAXI",
            "alternateName": "Kareem Taxi",
            "url": "https://www.kareemtaxi.co.mz/"
          })}
        </script>

        {/* WebPage Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": language === 'pt' ? "KAREEM TAXI - Parceiro Oficial da Yango em Maputo" : "KAREEM TAXI - Official Yango Partner in Maputo",
            "headline": language === 'pt' ? "KAREEM TAXI - Parceiro Oficial da Yango em Maputo" : "KAREEM TAXI - Official Yango Partner in Maputo",
            "url": "https://www.kareemtaxi.co.mz/"
          })}
        </script>

        <meta property="og:site_name" content="KAREEM TAXI" />

        <meta
          name="description" 
          content={t('home.meta.description')}
        />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={language === 'pt' ? 'KAREEM TAXI - Parceiro Oficial da Yango em Maputo' : 'KAREEM TAXI - Official Yango Partner in Maputo'} />
        <meta property="og:description" content={t('home.meta.description')} />
        <meta property="og:image" content="https://www.kareemtaxi.co.mz/images/kareem-taxi-parceiro-oficial-yango-mocambique.webp" />
        <meta property="og:url" content="https://www.kareemtaxi.co.mz/" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={language === 'pt' ? 'KAREEM TAXI - Parceiro Oficial da Yango em Maputo' : 'KAREEM TAXI - Official Yango Partner in Maputo'} />
        <meta name="twitter:description" content={t('home.meta.description')} />
        <meta name="twitter:image" content="https://www.kareemtaxi.co.mz/images/motorista-yango-maputo.webp" />
        
        {/* Canonical URL and hreflang alternates */}
        <link rel="canonical" href="https://www.kareemtaxi.co.mz/" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="alternate" href="https://www.kareemtaxi.co.mz/" hrefLang="pt" />
        <link rel="alternate" href="https://www.kareemtaxi.co.mz/?lang=en" hrefLang="en" />
        <link rel="alternate" href="https://www.kareemtaxi.co.mz/" hrefLang="x-default" />
        <meta property="og:locale" content="pt_MZ" />
        <meta property="og:locale:alternate" content="en_US" />
        
        {/* Additional Keywords Meta */}
        <meta name="keywords" content={language === 'pt' 
          ? 'cadastro motorista yango maputo, inscrição motorista yango, criar conta yango, registro yango moçambique, trabalhar com yango, motorista yango maputo, yango delivery maputo, yango taxi moçambique, yango tuktuk, yango pro download, treinamento yango pro, recarga yango mpesa, recarga yango emola, suporte motorista yango, parceiro oficial yango, quanto ganha motorista yango, requisitos motorista yango, como criar conta yango maputo, yango pro apk, download yango pro moçambique'
          : 'yango driver registration maputo, yango driver signup, create yango account, yango registration mozambique, work with yango, yango driver maputo, yango delivery maputo, yango taxi mozambique, yango tuktuk, yango pro download, yango pro training, yango recharge mpesa, yango recharge emola, yango driver support, official yango partner, yango driver earnings, yango driver requirements'
        } />
      </Helmet>

      <div className="min-h-screen overflow-x-hidden max-w-[100vw]">
        <Navigation />
        <main id="main-content" role="main">
          <Hero />
          
          <Suspense fallback={null}>
            <HeroGallery />
            <About />
            <Testimonials />
            <FAQ />
            <LazyBlogSection />
            <VagasMotorista />
            <QuickLinks />
            <DriverSection />
            <CoverageAreas />
          </Suspense>
        </main>
        <Suspense fallback={null}>
          <Footer />
          <FloatingWhatsApp />
        </Suspense>
      </div>
    </>
  );
};

export default Index;
